/**
 * Content-Plan selectors — testid registry for the ContentPlansPage tab,
 * CreateContentStrategyDialog, drill-down views, and PostDetailPanel.
 *
 * Single source of truth — all content-plan E2E tests import from this
 * registry (never hardcode testids, per Segmently test rule).
 */

export const CONTENT_PLAN = {
  // ── Page + Tabs ──
  /** @deprecated Content Plan is now a top-level admin route, not a parent tab.
   *  Use PAGE_ROOT after navigating to /project/{id}/content-plan. */
  TAB:                  'content-plans-tab',
  PAGE_ROOT:            'content-plans-page',

  // ── Phase 1 redesign: toast queue (no-silent-tab-switch contract) ──
  /** Container for the in-page toast/banner. Exists when a toast is showing. */
  TOAST:                'toast-content-plan',
  /** Toast action button — drives user to Strategies tab after creation. */
  TOAST_OPEN_STRATEGY:  'toast-action-open-strategy',
  /** Toast action button — drives user to Backlog tab after open-backlog event. */
  TOAST_OPEN_BACKLOG:   'toast-action-open-backlog',
  /** Toast action button — drives user to Backlog tab after topic gen. */
  TOAST_VIEW_BACKLOG:   'toast-action-view-backlog',
  SUBTAB_STRATEGIES:    'cp-subtab-strategies',
  SUBTAB_BACKLOG:       'cp-subtab-backlog',
  SUBTAB_THEMES:        'cp-subtab-themes',
  SUBTAB_TEMPLATES:     'cp-subtab-templates',
  SUBTAB_SERIES:        'cp-subtab-series',
  SUBTAB_SETTINGS:      'cp-subtab-settings',

  // ── Create-strategy dialog ──
  OPEN_DIALOG_BTN:      'create-content-strategy-open-btn',
  DIALOG:               'create-content-strategy-dialog',
  NAME_INPUT:           'content-strategy-name',
  DATERANGE:            'content-strategy-daterange',
  GOAL_RADIO:           'content-strategy-goal',
  PLATFORMS_MULTI:      'content-strategy-platforms',
  INSIGHTS_PICKER:      'content-strategy-insights',
  AUDIENCE_PICKER:      'content-strategy-audiences',
  SOURCE_DATA_PICKER:   'source-data-picker',
  MIX_PRESET:           'content-strategy-mix',
  PILLAR_MODE:          'content-strategy-pillarmode',
  TOV_EDITOR:           'tov-editor',
  DS_EDITOR:            'ds-editor',
  BRAND_ASSET_UPLOAD:   'brand-asset-upload',
  CR_ENABLE_CHECK:      'creator-research-enable',
  GENERATE_BTN:         'generate-content-strategy',
  CANCEL_BTN:           'create-content-strategy-cancel',
  CREATE_BLANK_BTN:     'create-blank-content-strategy',
  STRATEGY_ONE_SHOT_MODE:'strategy-auto-fill-backlog',
  STRATEGY_CADENCE_SECTION:'strategy-cadence-section',
  STRATEGY_CADENCE_PLATFORM_SECTION:'strategy-cadence-platform-section',
  STRATEGY_ADD_PLATFORM_BUTTON:'strategy-add-platform-button',
  STRATEGY_ADD_PLATFORM_OPTION:'strategy-add-platform-option',
  STRATEGY_CADENCE_PLATFORM_TOGGLE:'strategy-cadence-platform-toggle',
  STRATEGY_CADENCE_PLATFORM_REMOVE:'strategy-cadence-platform-remove',
  STRATEGY_PREFLIGHT_PANEL:'strategy-preflight-panel',

  // ── Model selector per agent ──
  MODEL_STRATEGY:       'model-strategy',
  MODEL_RESEARCH:       'model-research',
  MODEL_PLATFORM_POST:  'model-platform-post',

  // ── Strategies table (Level 1 in drill-down) ──
  STRATEGIES_TABLE:     'content-strategies-table',
  STRATEGY_ROW:         'content-strategy-row',
  STATUS_BADGE:         'content-strategy-status',

  // ── Sprints table (still Level 1 — inside strategy detail) ──
  SPRINTS_TABLE:        'sprints-table',
  SPRINT_ROW:           'sprint-row',
  THEME_QUOTA_BAR:      'theme-quota-bar',
  GENERATE_STRATEGY_POSTS: 'strategy-generate-all-posts',

  // ── Topics table (Level 2) ──
  TOPICS_TABLE:         'sprint-topics-table',
  TOPIC_ROW:            'topic-row',
  GENERATE_SPRINT_POSTS:'sprint-generate-posts',
  POST_GENERATION_HISTORY:'post-generation-history',
  GENERATION_HISTORY_LIST:'generation-history-list',
  GENERATION_HISTORY_ROW: 'generation-history-row-',
  GENERATION_REVIEW_DIALOG: 'generation-review-dialog',
  GENERATION_REVIEW_SELECT_ALL: 'generation-review-select-all',
  GENERATION_REVIEW_CANCEL: 'generation-review-cancel',

  // ── Topic detail (Level 3) ──
  TOPIC_DETAIL:         'topic-detail-view',
  POST_CARD_LINKEDIN:   'post-card-linkedin',
  POST_CARD_TWITTER:    'post-card-twitter',
  POST_CARD_INSTAGRAM:  'post-card-instagram',
  POST_CARD_TIKTOK:     'post-card-tiktok',
  POST_CARD_FACEBOOK:   'post-card-facebook',

  // ── Post detail panel (slide-over) ──
  POST_DETAIL_PANEL:    'post-detail-panel',
  POST_HOOK:            'post-hook',
  POST_BODY:            'post-body',
  POST_REGENERATE:      'post-regenerate-btn',
  WHY_FOR_AUDIENCE_CARD:'post-why-for-audience',
  DATA_MAPPING_VIEWER:  'post-data-mapping-viewer',
  SCHEDULED_AT:         'post-scheduled-at',

  // ── Breadcrumb (all levels) ──
  /** @deprecated Breadcrumb removed in popup-everywhere navigation redesign (2026-04-29).
   *  Drill-down now uses Drawer (Level 1 strategy detail) and SDialog (Level 2 sprint detail);
   *  closing the popup is the only "back" affordance needed. */
  BREADCRUMB_ROOT:      'cp-breadcrumb-root',
  /** @deprecated see BREADCRUMB_ROOT. */
  BREADCRUMB_STRATEGY:  'cp-breadcrumb-strategy',
  /** @deprecated see BREADCRUMB_ROOT. */
  BREADCRUMB_SPRINT:    'cp-breadcrumb-sprint',
  /** @deprecated see BREADCRUMB_ROOT. */
  BREADCRUMB_TOPIC:     'cp-breadcrumb-topic',

  // ── Strategy detail (Level 1) — Drawer over StrategiesTable ──
  /** Container of the Level-1 Drawer (right-anchored, ~80% width). */
  STRATEGY_DETAIL_DRAWER:        'strategy-detail-drawer',
  /** X-close button in the Drawer header. */
  STRATEGY_DETAIL_DRAWER_CLOSE:  'strategy-detail-drawer-close',

  // ── Sprint detail (Level 2) — SDialog stacked above the strategy Drawer ──
  /** Container of the Level-2 sprint detail dialog. */
  SPRINT_DETAIL_DIALOG:          'sprint-detail-dialog',

  // ── Approval flow ──
  APPROVE_STRATEGY:     'approve-strategy-btn',
  STRATEGY_DRAFT_REVIEW_PAGE: 'strategy-draft-review-page',
  STRATEGY_DRAFT_MODEL: 'strategy-draft-model',
  STRATEGY_DRAFT_REGENERATE: 'strategy-draft-regenerate',
  STRATEGY_DRAFT_APPROVE: 'strategy-draft-approve',

  // ── Topic Backlog sub-tab ──
  TOPIC_BACKLOG_TABLE:  'topic-backlog-table',
  TOPIC_BACKLOG_ROW:    'topic-backlog-row',
  OPEN_GEN_TOPICS:      'gen-topics-open-btn',
  TOPIC_GENERATION_HISTORY_BUTTON: 'topic-generation-history-button',
  TOPIC_GENERATION_HISTORY_DIALOG: 'topic-generation-history-dialog',
  ADD_TOPIC_MANUAL:     'add-topic-manual',
  GENERATE_TOPICS_DIALOG:'generate-topics-dialog',
  GEN_TOPICS_COUNT:     'gen-topics-count',
  GEN_TOPICS_THEMES:    'gen-topics-themes',
  GEN_TOPICS_PILLARS:   'gen-topics-pillars',
  GEN_TOPICS_AUTHOR_TOGGLE: 'gen-topics-author-toggle',
  GEN_TOPICS_DESCRIPTION: 'gen-topics-description',
  GEN_TOPICS_RESEARCH_MODE: 'gen-topics-research-mode',
  GEN_TOPICS_MODEL:     'gen-topics-model-tg',
  GEN_TOPICS_RESEARCH_MODEL: 'gen-topics-model-research',
  GEN_TOPICS_SUBMIT:    'gen-topics-submit',
  GEN_TOPICS_CANCEL:    'gen-topics-cancel',
  BACKLOG_FILTERS:      'backlog-filters',

  // ── Themes sub-tab ──
  THEMES_TAB:           'themes-tab',
  THEMES_NEW_BTN:       'themes-new-btn',
  THEMES_GENERATE_AI_BTN: 'themes-generate-ai-btn',
  THEMES_CATALOG_TABLE: 'themes-catalog-table',
  THEMES_HISTORY_SECTION: 'themes-history-section',
  THEME_GENERATE_DIALOG: 'theme-generate-dialog',
  THEME_GENERATE_PICKERS: 'theme-generate-pickers',
  THEME_GENERATE_HINT:  'theme-generate-hint',
  THEME_GENERATE_NOTES: 'theme-generate-notes',
  THEME_GENERATE_COUNT: 'theme-generate-count',
  THEME_GENERATE_COUNT_INPUT: 'theme-generate-count-input',
  THEME_GENERATE_MODEL: 'theme-generate-model',
  THEME_GENERATE_SUBMIT: 'theme-generate-submit',
  THEME_GENERATE_CANCEL: 'theme-generate-cancel',
  MANUAL_THEME_DIALOG:  'manual-theme-dialog',
  MANUAL_THEME_NAME:    'manual-theme-name',
  MANUAL_THEME_DATA_USAGE: 'manual-theme-data-usage',
  MANUAL_THEME_SUBMIT:  'manual-theme-submit',
  MANUAL_THEME_CANCEL:  'manual-theme-cancel',

  // ── Source inheritance audit (ToV/DS source chips) ──
  TOV_SOURCE_CHIP:      'tov-source-chip',
  DS_SOURCE_CHIP:       'ds-source-chip',

  // ── Apify cost estimator (dialog shown when CR enabled) ──
  COST_ESTIMATOR:       'apify-cost-estimator',

  // ── Creator References sub-tab ──
  CREATOR_REFS_ROOT:    'creator-references-panel',
  CREATORS_REFRESH_ALL: 'creators-refresh-all',
  CREATORS_ADD:         'creators-add',
  CREATORS_DISCOVER:    'creators-discover',
  CREATORS_TABLE:       'creators-table',
  CREATOR_DETAIL:       'creator-detail-panel',

  // ── Creator posts list (shown in detail dialog) ──
  CREATOR_POSTS_LIST:   'creator-posts-list',
  CREATOR_POST_ROW:     'creator-post-row',
  CREATOR_POST_LINK:    'creator-post-link',

  // ── Discovery history ──
  DISCOVERY_HISTORY:    'discovery-history',
  DISCOVERY_HISTORY_ROW:'discovery-history-row',

  // ── Discovery apply flow ──
  CREATOR_DISCOVER_APPLY: 'creator-discover-apply',

  // ── Creator init dialog ──
  CREATOR_INIT_DIALOG:      'creator-init-dialog',
  CREATOR_INIT_HANDLES:     'creator-init-handles',
  CREATOR_INIT_PLATFORM:    'creator-init-platform',
  CREATOR_INIT_CADENCE:     'creator-init-cadence',
  CREATOR_INIT_SUBMIT:      'creator-init-submit',
  CREATOR_INIT_CANCEL:      'creator-init-cancel',
  CREATOR_INIT_OPEN_DISCOVER: 'creator-init-open-discover',

  // ── Creator discovery dialog (AI-powered) ──
  CREATOR_DISCOVER_DIALOG:   'creator-discover-dialog',
  CREATOR_DISCOVER_KEYWORDS: 'creator-discover-keywords',
  CREATOR_DISCOVER_PLATFORM: 'creator-discover-platform',
  CREATOR_DISCOVER_SEARCH:   'creator-discover-search',
  CREATOR_DISCOVER_CANCEL:   'creator-discover-cancel',
  CREATOR_DISCOVER_ADD:      'creator-discover-add-selected',
  CREATOR_DISCOVER_CANDIDATE:'creator-discover-candidate',

  // ── Creator detail dialog (replaces old Drawer) ──
  CREATOR_DETAIL_DIALOG:     'creator-detail-dialog',
  CREATOR_DETAIL_RETRY:      'creator-detail-retry',
  CREATOR_DETAIL_CLOSE:      'creator-detail-close',

  // ── Scrape status chip (row + detail) ──
  CREATOR_STATUS_CHIP:       'creator-status-chip',

  // ── Calendar sub-tab (weeks × 7-days grid) ──
  SUBTAB_CALENDAR:           'cp-subtab-calendar',
  SUBTAB_ENGAGEMENT:         'cp-subtab-engagement',
  CALENDAR_PANEL:            'content-calendar-panel',
  CALENDAR_TOOLBAR:          'content-calendar-toolbar',
  CALENDAR_MODE_TOGGLE:      'content-calendar-mode-toggle',
  CALENDAR_MODE_STRATEGY:    'content-calendar-mode-strategy',
  CALENDAR_MODE_CROSS:       'content-calendar-mode-cross',
  CALENDAR_WINDOW_SIZE:      'content-calendar-window-size',
  CALENDAR_PREV_WINDOW:      'content-calendar-prev-window',
  CALENDAR_NEXT_WINDOW:      'content-calendar-next-window',
  CALENDAR_JUMP_TODAY:       'content-calendar-jump-today',
  CALENDAR_PLATFORM_FILTER:  'content-calendar-platform-filter',
  CALENDAR_ADD_PUBLICATION:  'content-calendar-add-publication',
  CALENDAR_GRID:             'content-calendar-grid',
  CALENDAR_WEEK_ROW:         'content-calendar-week-row',
  CALENDAR_DAY_CELL:         'content-calendar-day-cell',
  CALENDAR_DAY_CELL_EMPTY:   'content-calendar-day-cell-empty',
  CALENDAR_PUBLICATION_CHIP: 'content-calendar-publication-chip',
  CALENDAR_CHIP_OVERFLOW:    'content-calendar-chip-overflow',
  CALENDAR_LEGEND:           'content-calendar-legend',
  CALENDAR_LEGEND_ROW:       'content-calendar-legend-row',
  CALENDAR_LEGEND_TOGGLE:    'content-calendar-legend-toggle',
  CALENDAR_LEGEND_SOLO:      'content-calendar-legend-solo',
  ADD_PUB_DIALOG:            'add-publication-dialog',
  ADD_PUB_STRATEGY:          'add-publication-strategy',
  ADD_PUB_PLATFORM:          'add-publication-platform',
  ADD_PUB_TOPIC:             'add-publication-topic',
  ADD_PUB_TIME:              'add-publication-time',
  ADD_PUB_SAVE:              'add-publication-save',
  ADD_PUB_CANCEL:            'add-publication-cancel',
  CADENCE_EDITOR_DIALOG:     'platform-cadence-editor-dialog',
  CADENCE_PLATFORM_SECTION:  'platform-cadence-platform-section',
  CADENCE_ADD_PLATFORM:      'platform-cadence-add-platform',
  CADENCE_ADD_PLATFORM_OPTION:'platform-cadence-add-platform-option',
  CADENCE_PLATFORM_TOGGLE:   'platform-cadence-platform-toggle',
  CADENCE_PLATFORM_REMOVE:   'platform-cadence-platform-remove',
  CADENCE_POSTS_PER_WEEK:    'platform-cadence-posts-per-week',
  CADENCE_WEEKDAY:           'platform-cadence-weekday',
  CADENCE_TIME_CHIP:         'platform-cadence-time-chip',
  CADENCE_ADD_TIME:          'platform-cadence-add-time',
  CADENCE_TIMEZONE:          'platform-cadence-timezone',
  CADENCE_SAVE:              'platform-cadence-save',
  CADENCE_CANCEL:            'platform-cadence-cancel',

  // ── X manual delivery + engagement ──
  ENGAGEMENT_PANEL:          'content-plan-engagement-panel',
  ENGAGEMENT_SOURCES:        'engagement-listening-sources',
  ENGAGEMENT_ADD_SOURCE:     'engagement-add-source',
  ENGAGEMENT_SOURCE_DIALOG:  'engagement-source-dialog',
  ENGAGEMENT_REFRESH_PREVIEW:'engagement-refresh-preview',
  ENGAGEMENT_REFRESH_CONFIRM:'engagement-refresh-confirm',
  ENGAGEMENT_QUEUE:          'engagement-queue',
  ENGAGEMENT_STATUS_FILTER:  'engagement-status-filter',
  ENGAGEMENT_APPROVAL_DIALOG:'engagement-approval-dialog',
  ENGAGEMENT_REPLY_TEXT:     'engagement-reply-text',
  ENGAGEMENT_APPROVAL_CONFIRM:'engagement-approval-confirm',
  ENGAGEMENT_REPLY_PREVIEW:  'engagement-reply-preview',
  ENGAGEMENT_REPLY_CONFIRM:  'engagement-reply-confirm',
  engagementItem:            (id: string) => `engagement-item-${id}`,
  engagementRefresh:         (id: string) => `engagement-refresh-${id}`,
  engagementApprove:         (id: string) => `engagement-approve-${id}`,
  engagementReply:           (id: string) => `engagement-reply-${id}`,
  engagementReconcile:       (id: string) => `engagement-reconcile-${id}`,
  engagementDismiss:         (id: string) => `engagement-dismiss-${id}`,
  X_PUBLICATION_PANEL:       'x-manual-publication-panel',
  X_PUBLICATION_APPROVE:     'x-publication-approve',
  X_PUBLICATION_PREVIEW_SEND:'x-publication-preview-send',
  X_PUBLICATION_SEND_PREVIEW:'x-publication-send-preview',
  X_PUBLICATION_SEND_CONFIRM:'x-publication-send-confirm',
  X_PUBLICATION_RECONCILE:   'x-publication-reconcile',
  X_PUBLICATION_ANALYTICS:   'x-publication-analytics',
  X_ANALYTICS_COMPARISON:    'x-analytics-comparison',
  X_ANALYTICS_GROUP_BY:      'x-analytics-group-by',
  xAnalyticsGroup:           (key: string) => `x-analytics-group-${key}`,

  // ── Unified post-like surfaces (cell + dialog shared across post_example,
  //     content_post_draft, content_post, sprint_post) ──
  POST_LIKE_DIALOG:                'post-like-dialog',
  POST_LIKE_DIALOG_TITLE:          'post-like-dialog-title',
  POST_LIKE_DIALOG_PLATFORMS:      'post-like-dialog-platforms',
  POST_LIKE_DIALOG_HOOK:           'post-like-dialog-hook',
  POST_LIKE_DIALOG_BODY:           'post-like-dialog-body',
  POST_LIKE_DIALOG_CTA:            'post-like-dialog-cta',
  POST_LIKE_DIALOG_NOTES:          'post-like-dialog-notes',
  POST_LIKE_DIALOG_SOURCE_URL:     'post-like-dialog-source-url',
  POST_LIKE_DIALOG_SOURCE_HANDLE:  'post-like-dialog-source-handle',
  POST_LIKE_DIALOG_SAVE:           'post-like-dialog-save',
  POST_LIKE_DIALOG_CANCEL:         'post-like-dialog-cancel',
  POST_LIKE_DIALOG_DELETE:         'post-like-dialog-delete',
  POST_LIKE_DIALOG_PROMOTE:        'post-like-dialog-promote-to-example',
  POST_LIKE_DIALOG_AUDIT:          'post-like-dialog-audit-accordion',
  POST_LIKE_DIALOG_DATA_USAGE:     'post-like-dialog-data-usage-list',
  POST_LIKE_ASSETS_PREFIX:         'post-like-assets',
  FACEBOOK_PAGE_PUBLISH_PANEL:     'facebook-page-publish-panel',
  FACEBOOK_PAGE_PANEL_SUMMARY:     'facebook-page-panel-summary',
  FACEBOOK_PAGE_CONNECT_BUTTON:    'facebook-page-connect-button',
  FACEBOOK_PAGE_SELECT:            'facebook-page-select',
  FACEBOOK_PAGE_REFRESH_PAGES:     'facebook-page-refresh-pages',
  FACEBOOK_PAGE_SAVE_SELECTION:    'facebook-page-save-selection',
  FACEBOOK_PAGE_POST_MESSAGE:      'facebook-page-post-message',
  FACEBOOK_PAGE_POST_LINK:         'facebook-page-post-link',
  FACEBOOK_PAGE_PUBLISH_BUTTON:    'facebook-page-publish-button',
  FACEBOOK_PAGE_READBACK_BUTTON:   'facebook-page-readback-button',
  FACEBOOK_PAGE_DISCONNECT_BUTTON: 'facebook-page-disconnect-button',
  FACEBOOK_PAGE_PUBLISH_RESULT:    'facebook-page-publish-result',
  FACEBOOK_PAGE_RESULT_PERMALINK:  'facebook-page-result-permalink',

  // Row testid factories — UnifiedPostCell renders `post-like-row-{kind}-{id}`
  // by default, but callers often pass bespoke testIds (see `rowFor*` factories).
  POST_LIKE_ROW:                   'post-like-row',
  rowForPostExample:               (id: string) => `post-examples-row-${id}`,
  rowForDraft:                     (id: string) => `generation-draft-cell-${id}`,
  editDraftIcon:                   (id: string) => `generation-draft-edit-${id}`,
  rowForSprintDraft:               (id: string) => `sprint-draft-cell-${id}`,
  rowForSprintPost:                (id: string) => `sprint-post-row-${id}`,
  SPRINT_APPLIED_POSTS_ROOT:       'sprint-applied-posts',

  // ══════════════════════════════════════════════════════════════════════
  // Phase A creator-centric refactor (Sprints 18+) — testids registered
  // here so E2E specs and component code share a single source of truth.
  // ══════════════════════════════════════════════════════════════════════

  // ── ContentPlansPage top + sub tabs (H.1 consolidation) ──
  CONTENT_PLAN_TOP_TABS:           'content-plan-top-tabs',
  CONTENT_PLAN_TOP_CREATOR_SETUP:  'content-plan-top-creator-setup',
  CONTENT_PLAN_TOP_TOPICS_RESEARCH:'content-plan-top-topics-research',
  CONTENT_PLAN_TOP_PLANS:          'content-plan-top-plans',
  CONTENT_PLAN_SUB_TABS:           'content-plan-sub-tabs',
  SUBTAB_CREATOR:                  'content-plan-subtab-creator',
  SUBTAB_PLATFORMS:                'content-plan-subtab-platforms',

  // ── Author onboarding (Phase A) ──
  AUTHOR_ONBOARDING_STATUS_BANNER: 'author-onboarding-status-banner',
  AUTHOR_ONBOARDING_DIALOG:        'author-onboarding-dialog',
  AUTHOR_ONBOARDING_MODE_URL:      'author-onboarding-mode-url',
  AUTHOR_ONBOARDING_MODE_MANUAL:   'author-onboarding-mode-manual',
  AUTHOR_ONBOARDING_URL_INPUT:     'author-onboarding-url-input',
  AUTHOR_ONBOARDING_ABOUT_TEXTAREA:'author-onboarding-about-textarea',
  AUTHOR_ONBOARDING_POSTS_TEXTAREA:'author-onboarding-posts-textarea',
  AUTHOR_ONBOARDING_AUDIENCE_PICKER:'author-onboarding-audience-picker',
  AUTHOR_ONBOARDING_EXTRACT_BTN:   'author-onboarding-extract-btn',
  AUTHOR_ONBOARDING_CANCEL_BTN:    'author-onboarding-cancel-btn',
  AUTHOR_ONBOARDING_PROGRESS:      'author-onboarding-progress',

  // ── Multi-author workspace selector (URL-scoped author partition) ──
  AUTHOR_PICKER:                    'content-plan-author-picker',
  AUTHOR_PICKER_CREATE:             'author-picker-create',
  AUTHOR_PICKER_OVERVIEW:           'author-picker-overview',
  authorPickerCard:                 (id: string) => `author-picker-card-${id}`,
  AUTHOR_SELECTOR:                  'content-plan-author-selector',
  AUTHOR_SELECTOR_ALL:              'author-selector-all',
  AUTHOR_SELECTOR_CREATE:           'author-selector-create',
  authorSelectorItem:               (id: string) => `author-selector-item-${id}`,
  authorSelectorDefault:            (id: string) => `author-selector-default-${id}`,
  authorSelectorClone:              (id: string) => `author-selector-clone-${id}`,
  authorSelectorDelete:             (id: string) => `author-selector-delete-${id}`,
  CLONE_AUTHOR_DIALOG:              'clone-author-dialog',
  CLONE_AUTHOR_NAME:                'clone-author-name',
  CLONE_AUTHOR_INCLUDE_POSTS:       'clone-author-include-posts',
  CLONE_AUTHOR_CONFIRM:             'clone-author-confirm',
  DELETE_AUTHOR_DIALOG:             'delete-author-dialog',
  DELETE_AUTHOR_CONFIRM:            'delete-author-confirm',
  DELETE_AUTHOR_PREVIEW:            'delete-author-preview',

  // ── Creator export / import (Self-author JSON round-trip) ──
  CREATOR_TAB_ROOT:                'content-plan-creator-tab',
  CREATOR_TAB_ADD_AUTHOR_BTN:      'content-plan-creator-tab-add-author',
  CREATOR_TAB_EXPORT_BTN:          'content-plan-creator-tab-export-creator',
  CREATOR_TAB_IMPORT_BTN:          'content-plan-creator-tab-import-creator',
  CREATOR_TAB_REDO_BTN:            'content-plan-creator-tab-redo-onboarding',
  CREATOR_IMPORT_DIALOG:           'creator-import-dialog',
  CREATOR_IMPORT_FILE_INPUT:       'creator-import-file-input',
  CREATOR_IMPORT_PICK_FILE_BTN:    'creator-import-pick-file-btn',
  CREATOR_IMPORT_PREVIEW_CARD:     'creator-import-preview-card',
  CREATOR_IMPORT_REPLACE_WARNING:  'creator-import-replace-warning',
  CREATOR_IMPORT_CONFIRM_BTN:      'creator-import-confirm-button',

  // ── Topic resources (L.1) ──
  TOPIC_RESOURCES_SECTION:         'topic-resources-section',
  ADD_RESOURCE_BTN:                'add-resource-btn',
  ADD_RESOURCE_FORM:               'add-resource-form',
  RESOURCE_TYPE_SELECT:            'resource-type-select',
  RESOURCE_RELIABILITY_SELECT:     'resource-reliability-select',
  RESOURCE_CONTENT_INPUT:          'resource-content-input',
  RESOURCE_SOURCE_URL_INPUT:       'resource-source-url-input',
  RESOURCE_SAVE_BTN:               'resource-save-btn',
  RESOURCES_LIST:                  'resources-list',
  resourceRow:                     (id: string) => `resource-row-${id}`,
  resourceDeleteBtn:                (id: string) => `resource-${id}-delete-btn`,

  // ── Topic detail (G.0/G.1) ──
  TOPIC_DETAIL_PANEL:              'topic-detail-panel',
  /** Inner Box of TopicDetailPanel when rendered as the standalone Topic
   *  route (`/project/:pid/content-plan/topic/:tid`). Set on the outer
   *  flex container only when the panel is in fullScreen mode. */
  TOPIC_DETAIL_PANEL_FULLSCREEN:   'topic-detail-panel-fullscreen',
  /** "Open in new tab" IconButton on each Backlog row — opens the topic
   *  in a new browser tab via the standalone Topic route. */
  TOPIC_ROW_OPEN_IN_TAB:           (id: string) => `topic-row-open-in-tab-${id}`,
  /** Live status pill rendered by `<CellTaskBadge>` — subscribes to a backend
   *  task and shows phase displayName while the task is in `ACTIVE_STATUSES`.
   *  Plain testid for the generic case; specialised builders below for the
   *  three places it is wired (post cells, research run row, research stepper). */
  CELL_TASK_BADGE:                 'cell-task-badge',
  CELL_TASK_BADGE_ERROR:           'cell-task-badge-error',
  POSTS_GRID_MASTER_TASK_BADGE:    (aspectKey: string) => `posts-grid-master-${aspectKey}-task-badge`,
  POSTS_GRID_DERIVATIVE_TASK_BADGE: (aspectKey: string, platform: string, postId: string) =>
    `posts-grid-cell-${aspectKey}-${platform}-task-badge-${postId}`,
  RESEARCH_RUN_PHASE_TASK_BADGE:   (runId: string, phase: 'brief' | 'research' | 'analysis') =>
    `research-run-${runId}-${phase}-task-badge`,
  RESEARCH_STEP_BRIEF_TASK_BADGE:  'research-step-brief-task-badge',
  RESEARCH_STEP_DEEP_RESEARCH:     'research-step-deep-research',
  RESEARCH_STEP_ANALYSIS:          'research-step-analysis',
  TOPIC_RESEARCH_RUN_ALL:          'topic-research-run-all',
  RESEARCH_MODELS_TOGGLE:          'research-models-toggle',
  RESEARCH_MODEL_BRIEF:            'research-model-brief',
  RESEARCH_SEARCH_PROVIDER:        'research-search-provider',
  RESEARCH_DEPTH:                  'research-depth',
  RESEARCH_MODEL_RESEARCH:         'research-model-research',
  RESEARCH_REASONING_EFFORT:       'research-reasoning-effort',
  RESEARCH_MODEL_ANALYSIS:         'research-model-analysis',
  RESEARCH_STUDIES_TABLE:          'research-studies-table',
  RESEARCH_STUDY_ADD:              'research-add-study',
  RESEARCH_STUDY_DIALOG:           'research-study-dialog',
  RESEARCH_STUDY_MODELS_TOGGLE:    'research-study-models-toggle',
  RESEARCH_STUDY_MODEL_BRIEF:      'research-study-model-brief',
  RESEARCH_STUDY_SEARCH_PROVIDER:  'research-study-search-provider',
  RESEARCH_STUDY_DEPTH:            'research-study-depth',
  RESEARCH_STUDY_MODEL_RESEARCH:   'research-study-model-research',
  RESEARCH_STUDY_REASONING_EFFORT: 'research-study-reasoning-effort',
  RESEARCH_STUDY_MODEL_ANALYSIS:   'research-study-model-analysis',
  RESEARCH_STUDY_RUN_ALL:          'research-study-run-all',
  TOPIC_DETAIL_TABS:               'topic-detail-tabs',
  TOPIC_DETAIL_PILLARS:            'topic-detail-pillars',
  TOPIC_DETAIL_KEY_MESSAGE:        'topic-detail-key-message',
  TOPIC_DETAIL_COPE:               'topic-detail-cope',
  TOPIC_DETAIL_COPE_GENERATE_BTN:  'topic-detail-cope-generate-btn',
  TOPIC_DETAIL_COPE_MASTER:        'topic-detail-cope-master',
  TOPIC_TAB_OVERVIEW:              'topic-tab-overview',
  TOPIC_TAB_RESEARCH:              'topic-tab-research',
  TOPIC_TAB_TEMPLATES:             'topic-tab-templates',
  TOPIC_TAB_VOICE:                 'topic-tab-voice',
  TOPIC_TAB_POSTS:                 'topic-tab-posts',
  TOPIC_DESIGN_EDITOR:             'topic-design-editor',
  TOPIC_DESIGN_SAVE:               'topic-design-save',
  TOPIC_DESIGN_CLEAR:              'topic-design-clear',
  TOPIC_DESIGN_PRIMARY_COLOR:      'topic-design-primary-color',
  TOPIC_DESIGN_REFERENCE_SET_IMPORT: 'topic-design-reference-set-import',
  // ── Topic Templates tab (per-platform default template picker) ──
  TOPIC_TEMPLATES_TAB:             'topic-templates-tab',
  TOPIC_TEMPLATES_ROW:             'topic-templates-row',          // suffix with `-${platform}`
  TOPIC_TEMPLATES_CHOOSE_BTN:      'topic-templates-choose',       // suffix with `-${platform}`
  TOPIC_TEMPLATES_CLEAR_BTN:       'topic-templates-clear',        // suffix with `-${platform}`

  // ── Topic Posts tab — per-cell grid (Phase 2.5+) ──
  POSTS_GRID:                      'posts-grid',
  POSTS_GRID_ROW_LABEL:            'posts-grid-row-label',         // suffix with `-${aspectKey | 'noaspect'}`
  POSTS_GRID_MASTER_CELL:          'posts-grid-master',            // suffix with `-${aspectKey | 'noaspect'}`
  POSTS_GRID_MASTER_GENERATE:      'posts-grid-master',            // suffix `-${aspect}-generate`
  POSTS_GRID_CELL:                 'posts-grid-cell',              // suffix `-${aspect}-${platform}`
  POSTS_GRID_CELL_GENERATE:        'posts-grid-cell',              // suffix `-${aspect}-${platform}-generate`
  POSTS_GRID_SLOT_FOCUS_ALERT:     'posts-grid-slot-focus-alert',

  // ── Aspects edit modal (opens from Overview Plan preview) ──
  ASPECTS_EDIT_DIALOG:             'aspects-edit-dialog',
  TOPIC_DETAIL_PLAN:               'topic-detail-plan',
  TOPIC_DETAIL_EDIT_ASPECTS_BTN:   'topic-detail-edit-aspects',

  // ── AI aspect-generation dialog (opens from PostBreakdownEditor "✨ Generate aspects with AI") ──
  ASPECTS_GENERATE_BTN:            'aspects-generate-btn',
  ASPECTS_DIALOG:                  'aspects-generate-dialog',
  ASPECTS_COUNT:                   'aspects-generate-count',
  ASPECTS_MODEL:                   'aspects-generate-model',
  ASPECTS_INSTRUCTION:             'aspects-generate-instruction',
  ASPECTS_AUTHOR_TOGGLE:           'aspects-generate-author-toggle',
  ASPECTS_PILLARS:                 'aspects-generate-pillars',
  ASPECTS_SUBMIT:                  'aspects-generate-submit',
  ASPECTS_CANCEL:                  'aspects-generate-cancel',
  // ── Backlog bulk action ──
  BULK_ASPECTS_BTN:                'bulk-aspects-generate-btn',

  // ── Per-cell generation dialog (Phase 2.6) ──
  PER_CELL_GENERATE_DIALOG:        'per-cell-generate-dialog',
  PER_CELL_GENERATE_SUBMIT:        'per-cell-generate-submit',
  PER_CELL_SLOT_LOCK_SUMMARY:      'per-cell-slot-lock-summary',

  // ── Per-cell dialog inner controls (Phase 3.7+ — variants/series/fresh-variants) ──
  PER_CELL_METHOD_COPE:            'per-cell-method-cope',
  PER_CELL_METHOD_DIRECT:          'per-cell-method-direct',
  PER_CELL_MODE_SINGLE:            'per-cell-mode-single',
  PER_CELL_MODE_VARIANTS:          'per-cell-mode-variants',
  PER_CELL_MODE_FRESH_VARIANTS:    'per-cell-mode-fresh-variants',
  PER_CELL_MODE_SERIES:            'per-cell-mode-series',
  PER_CELL_SERIES_COUNT_SLIDER:    'per-cell-series-count-slider',
  PER_CELL_SERIES_COUNT_INPUT:     'per-cell-series-count-input',
  PER_CELL_SERIES_FLAVOR_SELECT:   'per-cell-series-flavor-select',
  PER_CELL_SERIES_FLAVOR_INPUT:    'per-cell-series-flavor-input',
  PER_CELL_FRESH_COUNT_SLIDER:     'per-cell-fresh-count-slider',
  PER_CELL_FRESH_COUNT_INPUT:      'per-cell-fresh-count-input',
  PER_CELL_ASSET_MODE_TOGGLE:      'per-cell-asset-mode-toggle',
  perCellSeriesFlavorOption:       (flavor: 'continuation' | 'counter_point' | 'how_to_sequence' | 'story_arc') =>
                                     `per-cell-series-flavor-${flavor}`,
  perCellAssetMode:                (mode: 'plan_and_generate' | 'plan_only' | 'text_only') =>
                                     `per-cell-asset-mode-${mode}`,

  // ── Phase 3 grid actions: cell items + master regen + row stale regen ──
  POSTS_GRID_CELL_ITEM:            'posts-grid-cell-item',         // suffix `-${aspect}-${platform}-item-${postId}`
  POSTS_GRID_CELL_PROMOTE:         'posts-grid-cell-promote',      // suffix `-${aspect}-${platform}-promote-${postId}`
  POSTS_GRID_CELL_DELETE:          'posts-grid-cell-delete',       // suffix `-${aspect}-${platform}-delete-${postId}`
  POSTS_GRID_MASTER_REGEN:         'posts-grid-master-regen',      // suffix `-${aspect}`
  POSTS_GRID_MASTER_DELETE:        'posts-grid-master-delete',     // suffix `-${aspect}`
  POSTS_GRID_ROW_REGEN_STALE:      'posts-grid-row-regen-stale',   // suffix `-${aspectKey | 'noaspect'}`

  // ── Strategy advanced + reactive slot buffer (G.0 / L.4) ──
  STRATEGY_ADVANCED_SECTION:       'strategy-advanced-section',
  STRATEGY_AUTO_ASSIGN_TOPICS:     'strategy-auto-assign-topics',
  STRATEGY_AUTO_GENERATE_FIRST_SPRINT:'strategy-auto-generate-first-sprint',
  STRATEGY_REACTIVE_SLOT_PCT:      'strategy-reactive-slot-pct',
  STRATEGY_AUTO_FILL_BACKLOG:      'strategy-auto-fill-backlog',

  // ── Topic pillar review actions ──
  TOPIC_PILLARS_CONFIRM_ALL:       'topic-pillars-confirm-all',

  // ── Creator-tab collapsible sections (SAccordion-based, post-2026-04-29 refactor).
  //    Each section persists its expanded/collapsed state in localStorage under
  //    `cp:section:{id}`. Section roots have these testids; inner content
  //    testids stay scoped to their concrete sub-component. ──
  AUTHOR_IDENTITY_SECTION:         'author-identity-editor',
  AUTHOR_IDENTITY_REANALYZE_BTN:   'author-identity-editor-reanalyzeBtn',
  TOPIC_PILLARS_SECTION:           'topic-pillars-section',
  PLATFORMS_SECTION:               'author-platforms-section',
  PLATFORMS_ADD_BTN:               'author-platforms-section-addBtn',
  PLATFORMS_TAB_ROOT:              'content-plan-platforms-tab',
  PLATFORM_OVERVIEW_LIST:          'platform-overview-list',
  platformCard:                    (platformId: string) => `platform-card-${platformId}`,
  platformRow:                     (platformId: string) => `platform-row-${platformId}`,
  platformOpenExternal:            (platformId: string) => `platform-${platformId}-expand-toggle`,
  platformInlineTabs:              (platformId: string) => `platform-${platformId}-tabs`,
  PLATFORM_WORKSPACE_DIALOG:       'content-plan-platform-workspace-dialog',
  PLATFORM_WORKSPACE_ROOT:         'content-plan-platform-workspace',
  PLATFORM_WORKSPACE_BACK:         'content-plan-platform-workspace-back',
  PLATFORM_WORKSPACE_SHELL:        'content-plan-platform-workspace-shell',
  PLATFORM_WORKSPACE_BODY:         'content-plan-platform-workspace-body',
  PLATFORM_WORKSPACE_TABS:         'content-plan-platform-workspace-tabs',
  platformWorkspaceTab:            (tab: string) => `content-plan-platform-workspace-tab-${tab}`,
  designPackageSummary:            (platformId: string) => `platform-${platformId}-design-package-summary`,
  designSlotMatrix:                (platformId: string) => `platform-${platformId}-design-slot-matrix`,
  designSlot:                      (platformId: string, slotKey: string) => `platform-${platformId}-design-slot-${slotKey}`,
  designSlotRefs:                  (platformId: string, slotKey: string) => `platform-${platformId}-design-slot-${slotKey}-refs`,
  designSlotDialog:                (platformId: string, slotKey: string) => `platform-${platformId}-${slotKey}-design-slot-dialog`,
  designSlotReferenceSetImport:    (platformId: string, slotKey: string) => `platform-${platformId}-${slotKey}-design-reference-set-import`,
  designSlotReferenceSetDialog:    (platformId: string, slotKey: string) => `platform-${platformId}-${slotKey}-reference-set-import-dialog`,
  designSlotApplyBaseColors:       (platformId: string) => `platform-${platformId}-apply-base-colors`,
  designSlotApplyBaseColorsDialog: (platformId: string) => `platform-${platformId}-apply-base-colors-dialog`,

  // ── PerAuthor templates + post examples (I.2 / I.3) ──
  PER_AUTHOR_TEMPLATE_ADD_BTN:     'per-author-template-add-btn',
  PER_AUTHOR_TEMPLATE_EDIT_FORM:   'per-author-template-edit-form',
  PER_AUTHOR_TEMPLATE_SAVE_BTN:    'per-author-template-save-btn',
  PER_AUTHOR_TEMPLATES_LIST:       'per-author-templates-list',
  TEMPLATE_FUNNEL_SELECT:          'template-funnel-select',
  TEMPLATE_TAG_SELECT:             'template-tag-select',
  PER_AUTHOR_POST_EXAMPLES_ADD_BTN:'per-author-post-examples-add-btn',
  PER_AUTHOR_POST_EXAMPLES_LIST:   'per-author-post-examples-list',

  // ── Concurrent Minsky refactor — author_posts unified surface ──
  AUTHOR_POST_EDITOR_DIALOG:       'author-post-editor-dialog',
  AUTHOR_POST_EDITOR_ADD_BUTTON:   'author-post-editor-add-button',
  AUTHOR_POSTS_LIBRARY_PICKER:     'author-posts-library-picker',
  UNIFIED_POST_PROMOTE_BUTTON:     'unified-post-promote-button',
  UNIFIED_POST_AUDIT_ACCORDION:    'unified-post-audit-accordion',

  // ── Platform voice editor (I.1 / L.5) ──
  PLATFORM_VOICE_EDITOR:           'platform-voice-editor',
  PLATFORM_VOICE_DIRTY_COUNT:      'platform-voice-editor-dirty-count',
  PLATFORM_VOICE_REEXTRACT_BTN:    'platform-voice-reextract-btn',
  PLATFORM_VOICE_INPUT:            'platform-voice-input',
  PLATFORM_STRUCTURE_INPUT:        'platform-structure-input',
  PLATFORM_VOCAB_USE_INPUT:        'platform-vocab-use-input',
  PLATFORM_VOCAB_AVOID_INPUT:      'platform-vocab-avoid-input',
  PLATFORM_AVOID_PATTERNS_INPUT:   'platform-avoid-patterns-input',
  PLATFORM_EMOJI_POLICY_SELECT:    'platform-emoji-policy-select',
  PLATFORM_HASHTAG_COUNT_INPUT:    'platform-hashtag-count-input',
  PLATFORM_HASHTAG_PRINCIPLE_INPUT:'platform-hashtag-principle-input',
  PLATFORM_NOTES_INPUT:            'platform-notes-input',
  PLATFORM_VOICE_SAVE_BTN:         'platform-voice-save-btn',

  // ── Voice diff panel ──
  VOICE_DIFF_PANEL:                'voice-diff-panel',
  VOICE_DIFF_PANEL_ROWS:           'voice-diff-panel-rows',
  VOICE_DIFF_PANEL_APPLY_BTN:      'voice-diff-panel-apply-btn',
  voiceDiffPanelRow:               (field: string) => `voice-diff-panel-row-${field}`,
  voiceDiffPanelApplyField:        (field: string) => `voice-diff-panel-apply-${field}`,
  voiceDiffPanelKeepField:         (field: string) => `voice-diff-panel-keep-${field}`,

  // ── Calendar topic backlog sidebar (H.2) ──
  TOPIC_BACKLOG_SIDEBAR:           'topic-backlog-sidebar',
  TOPIC_BACKLOG_SIDEBAR_SEARCH:    'topic-backlog-sidebar-search',
  topicBacklogGroup:               (pillarId: string) => `topic-backlog-sidebar-group-${pillarId}`,
  draggableTopicCard:              (topicId: string) => `draggable-topic-card-${topicId}`,

  // ── Calendar live status badges (H.3) ──
  CALENDAR_PUBLICATION_CHIP_GENERATING: 'content-calendar-publication-chip-generating',
  CALENDAR_PUBLICATION_CHIP_ERROR: 'content-calendar-publication-chip-error',

  // ── UnifiedPostDialog refine + multi-platform tabs (H.4) ──
  POST_LIKE_DIALOG_PLATFORM_TABS:  'post-like-dialog-platform-tabs',
  POST_LIKE_DIALOG_REFINE_BTN:     'post-like-dialog-refine-btn',
  POST_LIKE_DIALOG_REFINE_PROMPT:  'post-like-dialog-refine-prompt',
  POST_LIKE_DIALOG_REFINE_CONFIRM: 'post-like-dialog-refine-confirm',
  postLikeDialogPlatformTab:       (platform: string) => `post-like-dialog-platform-tab-${platform}`,

  // ══════════════════════════════════════════════════════════════════════
  // M2 wizard (Content Plan Flow Polish — S3.2/S3.3) + M3 Run-all (S4.6)
  // ══════════════════════════════════════════════════════════════════════

  // ── Wizard progress strip (collapsible layer ABOVE the tab shell) ──
  WIZARD_STRIP:                    'cp-wizard-strip',
  WIZARD_STRIP_TOGGLE:             'cp-wizard-strip-toggle',
  WIZARD_STRIP_SUMMARY:            'cp-wizard-strip-summary',
  /** One node per D12 step id (author|platforms|pillars|research|backlog|strategy|publications|posts|assets). */
  wizardStepNode:                  (stepId: string) => `cp-wizard-step-${stepId}`,
  WIZARD_STEP_CARD:                'cp-wizard-step-card',
  /** The ONE primary contained action on the expanded step card. */
  WIZARD_STEP_PRIMARY:             'cp-wizard-step-primary',
  /** Explicit "Open <sub-tab>" link — the ONLY wizard element that navigates. */
  WIZARD_STEP_OPEN_SUBTAB:         'cp-wizard-step-open-subtab',
  /** Locked-step annotation (reason + unlock link); never navigates. */
  WIZARD_STEP_LOCKED_ANNOTATION:   'cp-wizard-step-locked-annotation',
  /** Link inside the locked annotation that SELECTS the unlocking step's card. */
  WIZARD_STEP_UNLOCK_LINK:         'cp-wizard-step-unlock-link',
  /** Inline error area on the step card — CTA stays actionable on failure (AC-A042). */
  WIZARD_STEP_ERROR:               'cp-wizard-step-error',

  // ── Persistent task-progress banner (LT1 fix; wired to useContentStrategyTask) ──
  TASK_BANNER:                     'cp-task-banner',
  /** Full, untruncated error text region (LT2 rule: no hover-only errors). */
  TASK_BANNER_ERROR:               'cp-task-banner-error',
  TASK_BANNER_DETAILS:             'cp-task-banner-details',
  TASK_BANNER_DETAILS_DIALOG:      'cp-task-banner-details-dialog',
  TASK_BANNER_RETRY:               'cp-task-banner-retry',
  TASK_BANNER_DISMISS:             'cp-task-banner-dismiss',
  /** Opt-in toast CTA that expands the wizard strip to reveal the banner. */
  TOAST_VIEW_PROGRESS:             'toast-action-view-progress',

  // ── M3 Run-all (strip-header entry + confirm dialog + staged panel) ──
  RUNALL_BUTTON:                   'cp-runall-button',
  RUNALL_DIALOG:                   'cp-runall-dialog',
  RUNALL_TOGGLE_REVIEW_DRAFTS:     'cp-runall-toggle-review-drafts',
  RUNALL_TOGGLE_CONFIRM_PILLARS:   'cp-runall-toggle-confirm-pillars',
  RUNALL_CONFIRM:                  'cp-runall-confirm',
  RUNALL_PANEL:                    'cp-runall-panel',
  /** Stage ids: author | pillars | topics | strategy | posts. */
  runAllStageRow:                  (stageId: string) => `cp-runall-stage-${stageId}`,
  /** Primary action of a paused/errored stage row (suffix scoped by row testid). */
  RUNALL_STAGE_ACTION:             'cp-runall-stage-action',
  RUNALL_STAGE_SECONDARY:          'cp-runall-stage-secondary',
  RUNALL_STOP:                     'cp-runall-stop',
  RUNALL_DISMISS:                  'cp-runall-dismiss',

  // ── Dead-end fixes DE1–DE5 (empty-state CTAs) ──
  /** DE1 — pillars empty state: primary generate-from-profile CTA. */
  PILLARS_EMPTY_GENERATE_CTA:      'pillars-empty-generate-cta',
  /** DE1 — pillars empty state: secondary manual add CTA. */
  PILLARS_EMPTY_ADD_CTA:           'pillars-empty-add-cta',
  /** DE2 — empty calendar: explicit link to the Strategies sub-tab. */
  CALENDAR_EMPTY_OPEN_STRATEGIES_CTA: 'calendar-empty-open-strategies-cta',
  /** DE2 — empty calendar: opens the create-strategy dialog in place. */
  CALENDAR_EMPTY_CREATE_STRATEGY_CTA: 'calendar-empty-create-strategy-cta',
  /** DE3 — strategy detail "no sprint assignments": inline assignment action. */
  STRATEGY_ASSIGN_TOPICS_CTA:      'strategy-assign-topics-cta',
  /** DE4 — 0-topic sprint: inline generate-topics action. */
  SPRINT_GENERATE_TOPICS_CTA:      'sprint-generate-topics-cta',
  /** DE5 — overview author alert: opens the author picker in place. */
  OVERVIEW_PICK_AUTHOR_CTA:        'overview-pick-author-cta',
  OVERVIEW_AUTHOR_PICKER_DIALOG:   'overview-author-picker-dialog',

  // ── LT2 — CellTaskBadge persistent full-error surface ──
  /** Click the error badge → dialog with the FULL error text (not hover-only). */
  CELL_TASK_BADGE_ERROR_DIALOG:    'cell-task-badge-error-dialog',
} as const;

export type ContentPlanTestId = typeof CONTENT_PLAN[keyof typeof CONTENT_PLAN];
